import React, { useEffect, useState } from 'react'
import { useFormContext, useWatch } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { AnyAction } from 'redux';

import KogAutoCompleteControlled from '@/main/components/shared/inputAutoComplete/autoCompleteControlled';

import { KeyValue } from '@/main/models/common';
import { getTranslateByKey, isNullUndefined, isValueQueryParams } from '@/main/services/utils';
import { getCategory, resetReducerCategory, useCategoryReducer } from '@/main/store/category';
import { getSubcategory, resetReducerSubcategory, useSubcategoryReducer } from '@/main/store/subcategory';
import { getPositionWithIdPIES, resetReducerPosition, usePositionReducer } from '@/main/store/position';
import { SubcategoryRequest } from '@/main/models/request/subcategoryRequest';
import { PositionRequest } from '@/main/models/request';
import { useDinamicFiltersMappingCodes } from './dinamicFiltersMappingCodes';
import SelectXnuupPartIdAutoCompleteComponent from '@/main/components/shared/xnuupPartIdAutoComplete/selectXnuupPartIdAutoCompleteComponent';
import { XnuupPartIdAutoCompleteResponse } from '@/main/models/response';

interface CateogryDropdown extends KeyValue {
  ignoreWatcher?: boolean;
}
export const INITIAL_FORM_STATE_PART = {
  category: null as CateogryDropdown | null,
  subCategory: null as KeyValue | null,
  position: null as KeyValue | null,
  part: null as KeyValue | null,
  partName: '' as string,
};

export type IFormFiltersMappingTypePart = typeof INITIAL_FORM_STATE_PART

interface IFiltersMappingTypePartProps { }
function FiltersMappingTypePart({
}: IFiltersMappingTypePartProps) {
  const dispatch = useDispatch();
  const {
    isEdit,
    keyReducer,
    isRequiredField
  } = useDinamicFiltersMappingCodes();
  const {
    control,
    formState: { errors },
    register,
    clearErrors,
    resetField,
    setValue
  } = useFormContext<IFormFiltersMappingTypePart>();
  const watcherCategory = useWatch({
    control: control,
    name: 'category',
  });
  const watcherSubCategory = useWatch({
    control: control,
    name: 'subCategory',
  });
  const watcherPart = useWatch({
    control: control,
    name: 'part',
  });
  const [hasClearValue, setHasClearValue] = useState(false);

  const { category: categoryReducer } = useCategoryReducer();
  const { isolated: subcategoryReducer } = useSubcategoryReducer(keyReducer);
  const { isolated: positionReducer } = usePositionReducer(keyReducer);
  //#region Core Functions
  function resetReducers(keyFields: (keyof IFormFiltersMappingTypePart)[] = []) {
    const mapReducerField: { [K in keyof IFormFiltersMappingTypePart]?: (keyReducerAction?: string) => AnyAction } = {
      subCategory: resetReducerSubcategory,
      position: resetReducerPosition,
      part: resetReducerPosition,
    };
    const keysAllFields = Object.keys(mapReducerField) as (keyof IFormFiltersMappingTypePart)[];
    const fieldsToReset = keyFields.length === 0 ? keysAllFields : keyFields;

    Object.entries(mapReducerField)
      .forEach(([key, resetReducerField]) => {
        if (fieldsToReset.includes(key as keyof IFormFiltersMappingTypePart)) {
          dispatch(resetReducerField(keyReducer))
        }
      })

  }
  function resetFields(keyFields: (keyof IFormFiltersMappingTypePart)[] | keyof IFormFiltersMappingTypePart = []) {
    const arrayFields = Array.isArray(keyFields) ? keyFields : [keyFields];
    arrayFields.forEach((key) => {
      if (key === 'partName') setHasClearValue(true);
      resetField(key)
    })
    resetReducers(arrayFields)
  }
  //#endregion

  //#region Call API Domains
  function callCategoryApiDomain() {
    if (!isValueQueryParams(watcherCategory)) {
      resetFields(['category', 'subCategory', 'position', 'part'])
    }
    if (isNullUndefined(categoryReducer.data)) dispatch(getCategory());
  }

  function callSubCategoryApiDomain(categorySelected: CateogryDropdown | null) {
    if (!isValueQueryParams(categorySelected) && isNullUndefined(categorySelected?.ignoreWatcher)) {
      resetFields(['subCategory', 'part', 'position', 'partName'])
    }

    if (isNullUndefined(categorySelected)) return;
    const request: SubcategoryRequest = {
      guid: categorySelected.key as string,
      isCanastaBasica: true
    }
    dispatch(getSubcategory(request, keyReducer));
  }

  function loadOptionsByXnuupPartId(partId: XnuupPartIdAutoCompleteResponse) {
    if (!partId) return resetField('part')
    setValue('partName', partId.partPIESName);

    const {
      partCategoryGuid,
      partCategoryName,
      partGuid,
      partPIESName,
      partSubCategoryGuid,
      partSubCategoryName,
      piesPartTerminologyId
    } = partId;

    if (partCategoryGuid && partCategoryName) {
      clearErrors();
      setValue('category', {
        key: partCategoryGuid,
        value: partCategoryName,
        ignoreWatcher: true
      });
      if (isNullUndefined(categoryReducer.data)) dispatch(getCategory());

      if (partSubCategoryGuid && partSubCategoryName) {

        setValue('subCategory', {
          key: partSubCategoryGuid,
          value: partSubCategoryName
        });
        const request: SubcategoryRequest = {
          guid: String(partCategoryGuid),
          isCanastaBasica: true
        };
        dispatch(getSubcategory(request, keyReducer));

        if (partGuid) {
          const request: PositionRequest = {
            partGuid: String(partGuid)
          };
          dispatch(getPositionWithIdPIES(request, keyReducer));
        } else {
          dispatch(resetReducerPosition(keyReducer));
          setValue('position', null);
        }
      } else {
        dispatch(resetReducerSubcategory(keyReducer));
        setValue('subCategory', null);
      }
    } else {
      setValue('category', null);
    }

    setValue('part', {
      id: piesPartTerminologyId,
      key: partGuid,
      value: partPIESName
    } as KeyValue);
  }
  //#endregion

  useEffect(() => {
    callCategoryApiDomain();
    return () => {
      resetReducers()
    }
  }, [])

  const disabledCategoryReducer = isNullUndefined(categoryReducer.data);
  const disabledSubCategoryReducer = isNullUndefined(subcategoryReducer.data);
  const disabledPositionReducer = isNullUndefined(positionReducer.data);

  return (
    <>
      <KogAutoCompleteControlled
        id='id-mappingCode-select-category'
        nameCustom='category'
        label={getTranslateByKey('quoteCard.category')}
        isDefaultValueUnique
        control={control}
        setValueCustom={setValue}
        options={categoryReducer.data}
        isLoading={categoryReducer.isLoading}
        disabled={disabledCategoryReducer}
        rulesCustom={{ required: isRequiredField('category') }}
        onChangeCustom={(optionSelected) => {callSubCategoryApiDomain(optionSelected);}}
      />
      <KogAutoCompleteControlled
        id='id-mappingCode-select-subCategory'
        nameCustom='subCategory'
        label={getTranslateByKey('quoteCard.subCategory')}
        isDefaultValueUnique
        control={control}
        setValueCustom={setValue}
        options={subcategoryReducer.data}
        isLoading={subcategoryReducer.isLoading}
        disabled={disabledSubCategoryReducer}
        rulesCustom={{ required: isRequiredField('subCategory') }}
        onChangeCustom={() => { }}
      />
      <SelectXnuupPartIdAutoCompleteComponent
        ignoreQueryParams={keyReducer !== 'id-mappingCodes-filters'}
        required={isRequiredField('part')}
        partPIESName={watcherPart?.value}
        watcherCategory={watcherCategory}
        watcherSubcategory={watcherSubCategory}
        handleChangeXnuupPartId={(partId) => {
          loadOptionsByXnuupPartId(partId);
        }}
        errorMessageCustom={
          errors.part?.type === 'required'
            ? getTranslateByKey('information.xnuupPartIdRequired', true)
            : ''
        }
        hasClearValue={hasClearValue}
        setHasClearValue={setHasClearValue}
        hasErrorCustom={!!errors.part}
        label={getTranslateByKey('information.part')}
        showIdPIES={true}
        propsAutoComplete={{
          disabled: false,
        }}
        {...register('part', { required: isRequiredField('part') })}
      />
      <KogAutoCompleteControlled
        id='id-mappingCode-select-position'
        nameCustom='position'
        label={getTranslateByKey('information.position')}
        isDefaultValueUnique
        control={control}
        setValueCustom={setValue}
        options={positionReducer.data}
        isLoading={positionReducer.isLoading}
        disabled={disabledPositionReducer}
        rulesCustom={{ required: isRequiredField('position') }}
        onChangeCustom={() => { }}
      />
    </>
  )
}

export default FiltersMappingTypePart